import { supabase } from "@/integrations/supabase/client";
import {
  clearStaffSession,
  getStaffSessionToken,
  setStaffSession,
} from "@/services/staffSession";

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'operations_manager' | 'clinic_incharge_both' | 'clinic_incharge_forum' | 'clinic_incharge_ngeeann' | 'office_admin' | 'office_user';
  allowedClinics: string[];
  isActive: boolean;
  staffId?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

// Sign-in is server-side (/api/app-session: rate limited, HttpOnly cookie).
// That cookie proves who the user is to OUR server, but the patient tables are
// read straight from the browser with the publishable key, so the database
// still cannot tell a nurse from a stranger. The server therefore also hands
// back a staff session token (issue_staff_session, migration 20260930100000),
// which every patient_cases / patient_case_activities call now carries.
//
// staffSession is absent when the database has not had that migration yet;
// the patient services then fall back to the direct table path, which is
// correct because a database without staff sessions has not been locked down.
type StaffSessionPayload = { token: string; expiresAt: string } | null | undefined;

const storeStaffSession = (staffSession: StaffSessionPayload): void => {
  if (staffSession && typeof staffSession.token === 'string') {
    setStaffSession(staffSession.token, staffSession.expiresAt ?? null);
  }
};

type RpcCaller = (
  fn: string,
  args: Record<string, unknown>
) => Promise<{ data: unknown; error: { code?: string; message: string } | null }>;

/** A function that does not exist yet on this database, rather than a real failure. */
const isMissingFunction = (error: { code?: string; message?: string } | null): boolean => {
  if (!error) return false;
  const message = (error.message ?? '').toLowerCase();
  return (
    error.code === 'PGRST202' ||     // PostgREST: no function matches
    error.code === '42883' ||        // Postgres: undefined_function
    message.includes('could not find the function')
  );
};

/**
 * Is the token this browser holds still accepted by the database? 'unknown'
 * (network blip, or the migration not applied) keeps it: a blip must not sign
 * anyone out, and without the migration there is nothing to check against.
 */
const staffTokenState = async (): Promise<'live' | 'dead' | 'unknown'> => {
  const token = getStaffSessionToken();
  if (!token) return 'dead';
  try {
    const { data, error } = await (supabase.rpc as unknown as RpcCaller)('current_staff_session', {
      p_session_token: token,
    });
    if (error) return isMissingFunction(error) ? 'unknown' : 'dead';
    return Array.isArray(data) && data.length > 0 ? 'live' : 'dead';
  } catch {
    return 'unknown';
  }
};

export const authService = {
  async login(credentials: LoginCredentials): Promise<{ user: User | null; error: string | null }> {
    try {
      const response=await fetch('/api/app-session',{method:'POST',credentials:'same-origin',headers:{'content-type':'application/json'},body:JSON.stringify(credentials)});
      const result=await response.json();
      if(!response.ok||!result.user)return {user:null,error:response.status===429?'Too many attempts. Please try again later.':'Could not sign in. Check your details and try again.'};
      const user:User=result.user;

      clearStaffSession();
      storeStaffSession(result.staffSession);

      // Store user session safely
      try {
        localStorage.setItem('userSession', JSON.stringify(user));
      } catch (storageError) {
        console.warn('Failed to persist user session to localStorage:', storageError);
      }
      
      return { user, error: null };
    } catch (error) {
      console.error('Login exception:', error);
      return { user: null, error: 'An unexpected error occurred' };
    }
  },

  async logout(): Promise<void> {
    // Best effort: kill the staff token server-side so it is dead everywhere,
    // not just in this browser. A failure here must not block signing out.
    const token = getStaffSessionToken();
    if (token) {
      try {
        await (supabase.rpc as unknown as RpcCaller)('logout_staff', { p_session_token: token });
      } catch (error) {
        console.warn('logout_staff failed; clearing the token locally anyway:', error);
      }
    }
    clearStaffSession();

    const response=await fetch('/api/app-session',{method:'DELETE',credentials:'same-origin'});
    if(!response.ok)throw new Error('Sign-out could not be confirmed. Please retry.');
    try {
      localStorage.removeItem('userSession');
    } catch (storageError) {
      console.warn('Failed to remove user session from localStorage:', storageError);
    }
  },

  /**
   * On page load: the cookie decides whether the user is signed in. If this
   * browser has no live staff token (it signed in before staff sessions
   * existed, or the token expired or was revoked), ask the server to issue one
   * against the verified cookie. No cookie means the login screen, token or not.
   */
  async restoreSession():Promise<User|null>{
    const tokenState=await staffTokenState();
    if(tokenState==='dead')clearStaffSession();
    const url=tokenState==='dead'?'/api/app-session?staff_session=1':'/api/app-session';
    const response=await fetch(url,{credentials:'same-origin',cache:'no-store'});
    if(!response.ok){clearStaffSession();localStorage.removeItem('userSession');return null;}
    const result=await response.json();const user=result.user as User;
    storeStaffSession(result.staffSession);
    localStorage.setItem('userSession',JSON.stringify(user));return user;
  },

  getCurrentUser(): User | null {
    const sessionData = localStorage.getItem('userSession');
    if (!sessionData) return null;
    
    try {
      return JSON.parse(sessionData) as User;
    } catch {
      return null;
    }
  },

  canAccessClinic(user: User | null, clinicId: string): boolean {
    if (!user) return false;
    
    // Admin can access all clinics
    if (user.role === 'admin' || user.allowedClinics.includes('all')) return true;
    
    // Role-specific validation with allowed_clinics override
    switch (user.role) {
      case 'clinic_incharge_ngeeann':
      case 'clinic_incharge_forum':
      case 'clinic_incharge_both':
        // First check allowed_clinics array (user-assigned permissions)
        if (user.allowedClinics && user.allowedClinics.length > 0) {
          return user.allowedClinics.includes(clinicId);
        }
        // Fallback to role-based defaults only if no explicit permissions set
        if (user.role === 'clinic_incharge_ngeeann') return clinicId === 'ngeeann';
        if (user.role === 'clinic_incharge_forum') return clinicId === 'forum';
        if (user.role === 'clinic_incharge_both') return clinicId === 'forum' || clinicId === 'ngeeann';
        return false;
      case 'operations_manager':
        // Check allowed_clinics first, fallback to both clinics
        if (user.allowedClinics && user.allowedClinics.length > 0) {
          return user.allowedClinics.includes(clinicId);
        }
        return clinicId === 'forum' || clinicId === 'ngeeann';
      default:
        // For office_admin and office_user, check the allowed_clinics array
        return user.allowedClinics.includes(clinicId);
    }
  },

  canManageUsers(user: User | null): boolean {
    if (!user) return false;
    return user.role === 'admin';
  },

  canAccessAdminPanel(user: User | null): boolean {
    if (!user) return false;
    // Admin, Operations Manager, and all Clinic In-Charge roles can access admin panel
    return ['admin', 'operations_manager', 'clinic_incharge_both', 'clinic_incharge_forum', 'clinic_incharge_ngeeann'].includes(user.role);
  },

  canAccessPatients(user: User | null): boolean {
    if (!user) return false;
    return ['admin', 'operations_manager', 'office_admin', 'office_user'].includes(user.role);
  },

  canAccessInventory(user: User | null): boolean {
    if (!user) return false;
    return ['admin', 'operations_manager', 'office_admin', 'office_user'].includes(user.role);
  },

  canSeeAllTasks(user: User | null): boolean {
    if (!user) return false;
    return user.role === 'admin' || user.role === 'office_admin' || user.role === 'operations_manager';
  },

  canEditTaskDeadline(user: User | null): boolean {
    if (!user) return false;
    // Only admin can edit task deadlines
    return user.role === 'admin';
  },

  canDeleteTasks(user: User | null): boolean {
    if (!user) return false;
    // Only admin can delete tasks
    return user.role === 'admin';
  },

  canManageClinic(user: User | null, clinicId: string): boolean {
    if (!user) return false;
    
    // Admin can manage all clinics
    if (user.role === 'admin') return true;
    
    // Operations manager can manage both clinics
    if (user.role === 'operations_manager') return true;
    
    // Clinic in-charge can manage their specific clinic(s)
    if (user.role === 'clinic_incharge_both') return true;
    if (user.role === 'clinic_incharge_forum' && clinicId === 'forum') return true;
    if (user.role === 'clinic_incharge_ngeeann' && clinicId === 'ngeeann') return true;
    
    return false;
  },

  getUserAccessLevel(user: User | null): 'none' | 'user' | 'clinic_manager' | 'operations' | 'admin' {
    if (!user) return 'none';
    
    switch (user.role) {
      case 'admin':
        return 'admin';
      case 'operations_manager':
        return 'operations';
      case 'clinic_incharge_both':
      case 'clinic_incharge_forum':
      case 'clinic_incharge_ngeeann':
        return 'clinic_manager';
      case 'office_admin':
      case 'office_user':
        return 'user';
      default:
        return 'none';
    }
  }
};
